import { InteractionType, PublicClientApplication } from '@azure/msal-browser'
import { AuthCodeMSALBrowserAuthenticationProvider } from '@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { getProfilePhoto, getUser } from '../core/services/GraphService'
import { setDatadogUser } from '../datadogConfig'
import { msalInstance } from '../msalInstance'
import { apiClient } from '../api'

export interface GraphUser {
  displayName?: string
  email?: string
  avatar?: string
  id?: string
}

export interface AuthError {
  message: string
}

interface AuthContextType {
  user?: GraphUser
  isAuthenticated: boolean
  signIn: () => Promise<void>
  signInWithPassword: (username: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  refreshUser: () => Promise<void>
  sessionId?: string
}

const AuthContext = createContext<AuthContextType>({
  user: undefined,
  isAuthenticated: false,
  signIn: async () => {
    throw new Error('AuthContext used outside AuthProvider')
  },
  signInWithPassword: async () => {
    throw new Error('AuthContext used outside AuthProvider')
  },
  signOut: async () => {
    throw new Error('AuthContext used outside AuthProvider')
  },
  refreshUser: async () => {
    throw new Error('AuthContext used outside AuthProvider')
  },
  sessionId: undefined,
})

const JWT_TOKEN_KEY = 'jwt_token'

export const useAuth = () => useContext(AuthContext)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<GraphUser | undefined>(undefined)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [sessionId, setSessionId] = useState<string | undefined>(undefined)
  const isAuthDisabled = process.env.REACT_APP_DISABLE_AUTH === 'true'

  const authProvider = useMemo(() => {
    if (isAuthDisabled) return null
    return new AuthCodeMSALBrowserAuthenticationProvider(msalInstance as PublicClientApplication, {
      account: msalInstance.getActiveAccount()!,
      scopes: ['user.read'],
      interactionType: InteractionType.Redirect,
    })
  }, [isAuthDisabled])

  const fetchGraphUser = useCallback(async () => {
    if (!authProvider) return
    try {
      const graphUser = await getUser(authProvider)
      let avatar = ''
      try {
        avatar = await getProfilePhoto(authProvider)
      } catch {
        avatar = ''
      }
      const session = uuidv4()
      setUser({
        displayName: graphUser.displayName || '',
        email: graphUser.mail || graphUser.userPrincipalName || '',
        avatar,
        id: graphUser.id || '',
      })
      setSessionId(session)
      setIsAuthenticated(true)
      if (process.env.NODE_ENV !== 'development') {
        setDatadogUser({
          id: graphUser.id || '',
          user: graphUser.displayName || '',
          email: graphUser.mail || graphUser.userPrincipalName || '',
          sessionId: session,
        })
      }
    } catch (err: any) {
      if (err && err.message) {
        setUser(undefined)
        setIsAuthenticated(false)
        setSessionId(undefined)
        setDatadogUser({ id: '' })
      }
    }
  }, [authProvider])

  const fetchUserFromApi = useCallback(async () => {
    try {
      const userInfo = await apiClient.user.fetchUserInfo()
      const session = uuidv4()
      setUser({
        displayName: userInfo.username || '',
        email: userInfo.email || '',
        avatar: '',
        id: userInfo.email || '',
      })
      setSessionId(session)
      setIsAuthenticated(true)
      if (process.env.NODE_ENV !== 'development') {
        setDatadogUser({
          id: userInfo.email || '',
          user: userInfo.username || '',
          email: userInfo.email || '',
          sessionId: session,
        })
      }
    } catch (err: any) {
      setUser(undefined)
      setIsAuthenticated(false)
      setSessionId(undefined)
      setDatadogUser({ id: '' })
      localStorage.removeItem(JWT_TOKEN_KEY)
      throw err
    }
  }, [])

  // Expose refreshUser for manual refresh
  const refreshUser = useCallback(async () => {
    await fetchGraphUser()
  }, [fetchGraphUser])

  const signIn = useCallback(async () => {
    await msalInstance.loginRedirect({
      scopes: ['user.read'],
      prompt: 'select_account',
    })
    // After redirect, MSAL will handle the rest
  }, [])

  const signInWithPassword = useCallback(
    async (username: string, password: string) => {
      const response = await apiClient.auth.loginWithPassword(username, password)
      localStorage.setItem(JWT_TOKEN_KEY, response.access_token)
      await fetchUserFromApi()
    },
    [fetchUserFromApi],
  )

  const signOut = useCallback(async () => {
    const jwtToken = localStorage.getItem(JWT_TOKEN_KEY)
    setUser(undefined)
    setIsAuthenticated(false)
    setSessionId(undefined)
    setDatadogUser({ id: '' })
    localStorage.removeItem(JWT_TOKEN_KEY)
    if (!jwtToken) {
      await msalInstance.logoutRedirect()
    }
  }, [])

  const initializeAuth = useCallback(async () => {
    try {
      const jwtToken = localStorage.getItem(JWT_TOKEN_KEY)
      if (jwtToken) {
        await fetchUserFromApi()
        return
      }

      // Wait for MSAL to finish processing redirect (if any)
      const redirectResponse = await msalInstance.handleRedirectPromise()

      if (redirectResponse && redirectResponse.account) {
        // Just returned from authentication - user is authenticated
        fetchGraphUser()
      } else {
        // Normal page load - check for existing session
        const account = msalInstance.getActiveAccount()
        const allAccounts = msalInstance.getAllAccounts()

        if (account) {
          fetchGraphUser()
        } else if (allAccounts.length > 0) {
          msalInstance.setActiveAccount(allAccounts[0])
          fetchGraphUser()
        } else {
          setUser(undefined)
          setIsAuthenticated(false)
          setSessionId(undefined)
          setDatadogUser({ id: '' })
        }
      }
    } catch (error) {
      setUser(undefined)
      setIsAuthenticated(false)
      setSessionId(undefined)
      setDatadogUser({ id: '' })
    }
  }, [fetchGraphUser, fetchUserFromApi])

  useEffect(() => {
    if (isAuthDisabled) {
      const session = uuidv4()
      setUser({
        displayName: 'Demo User',
        email: 'demo@example.com',
        id: 'demo-user-id',
      })
      setIsAuthenticated(true)
      setSessionId(session)
    } else {
      initializeAuth()
    }
  }, [isAuthDisabled, initializeAuth])

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        signIn,
        signInWithPassword,
        signOut,
        refreshUser,
        sessionId,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
